import type { ProductType } from "@/context/AppContext";
import type { ProductPricingProfile } from "@/types/productOpportunity";

/**
 * Legacy profile preserves the existing bundleEngine economics
 * (MARKET_TARGET_RATIO=0.8, FEE_RATE=0.13, SHIPPING_RESERVE=$1) so opportunities
 * generated for legacy product types produce identical numbers when run
 * through the new pricing pipeline.
 */
export const LEGACY_PRICING_PROFILE: ProductPricingProfile = {
  marketRatio: 0.8,
  platformFeeRate: 0.13,
  paymentFeeRate: 0,
  shippingReserve: 1.0,
  packagingReserve: 0,
  minMarginPct: 0,
};

const PROFILES: Record<ProductType, ProductPricingProfile> = {
  // Legacy product types — kept on the historical 0.8/0.13/$1 triple to
  // preserve bundleEngine economics.
  "100-card Random Lot": LEGACY_PRICING_PROFILE,
  "50-card Themed Lot": LEGACY_PRICING_PROFILE,
  "25-card Trainer Lot": LEGACY_PRICING_PROFILE,
  "Holo/Reverse Bundle": LEGACY_PRICING_PROFILE,
  "4x Playset": LEGACY_PRICING_PROFILE,
  "Mystery Pack": LEGACY_PRICING_PROFILE,
  "Starter/Kid Pack": LEGACY_PRICING_PROFILE,
  "Premium Single": LEGACY_PRICING_PROFILE,
  "Sealed Bundle": LEGACY_PRICING_PROFILE,
  Custom: LEGACY_PRICING_PROFILE,
  // ── Studio product types — use the per-type ratios from the spec ──
  "Pre-built Deck": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.7 },
  "Deck Core": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.75 },
  "Deck Upgrade Kit": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.8 },
  "Set Builder Lot": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.7 },
  "Character Theme Bundle": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.75 },
  "Type Theme Bundle": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.7 },
  "Trainer Staples Kit": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.8 },
  "Energy Kit": { ...LEGACY_PRICING_PROFILE, marketRatio: 0.5 },
  // Imported products have no local inventory yet — fall back to the legacy
  // profile since there's no per-type economics until inventory is linked.
  Imported: LEGACY_PRICING_PROFILE,
};

export function getPricingProfile(
  productType: ProductType,
): ProductPricingProfile {
  return PROFILES[productType] ?? LEGACY_PRICING_PROFILE;
}

export interface PricedEconomics {
  expectedSalePrice: number;
  estimatedCostBasis: number;
  expectedProfit: number;
  expectedMarginPct: number;
}

/**
 * Profile-aware economics calculator. Mirrors the old bundleEngine.buildEconomics
 * shape but reads ratios/fees from the profile and supports an external
 * fulfillment cost in place of the flat shippingReserve.
 */
export function buildPricedEconomics(args: {
  estimatedMarketValue: number;
  estimatedCostBasis: number;
  productType: ProductType;
  /** When provided, overrides profile.shippingReserve. */
  fulfillmentCost?: number;
  /** When provided, overrides profile.packagingReserve. */
  packagingCost?: number;
}): PricedEconomics {
  const profile = getPricingProfile(args.productType);
  const expectedSalePrice = args.estimatedMarketValue * profile.marketRatio;
  const feeRate = profile.platformFeeRate + profile.paymentFeeRate;
  const shipping = args.fulfillmentCost ?? profile.shippingReserve;
  const packaging = args.packagingCost ?? profile.packagingReserve;
  const expectedProfit =
    expectedSalePrice * (1 - feeRate) -
    args.estimatedCostBasis -
    shipping -
    packaging;
  const expectedMarginPct =
    expectedSalePrice > 0 ? expectedProfit / expectedSalePrice : 0;
  return {
    expectedSalePrice,
    estimatedCostBasis: args.estimatedCostBasis,
    expectedProfit,
    expectedMarginPct,
  };
}
